<template>
	<div ref="imageContainer" class="uk-text-center lazy-image image-wrapper">
		<div v-if="!isLoaded" class="shimmer-effect"></div>

		<transition name="explicit-fade">
			<div
				v-if="isLoaded && explicit && !explicitDismissed"
				class="explicit-overlay"
				@click.stop
			>
				<div class="explicit-overlay__content">
					<div class="explicit-overlay__title">Expliciete inhoud</div>
					<div class="explicit-overlay__text">Klikken = kijken op eigen risico</div>
					<button
						type="button"
						class="explicit-overlay__btn uk-button uk-button-primary uk-button-small"
						@click.stop="dismissExplicit"
					>
						Tonen
					</button>
				</div>
			</div>
		</transition>

		<a v-if="link" :href="link" target="_blank">
			<img
				v-if="isLoaded"
				:src="src"
				:alt="alt"
				:class="imageClassName"
				:style="imageStyle"
				@load="handleLoad"
				@error="handleError"
			>
		</a>

		<img
			v-else-if="isLoaded"
			:src="src"
			:alt="alt"
			:class="imageClassName"
			:style="imageStyle"
			@load="handleLoad"
			@error="handleError"
			@click="handleClick"
		>
	</div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, getCurrentInstance } from 'vue';

import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

const props = defineProps({
	index: Number,
	src: String,
	alt: String,
	imageClassName: String,
	explicit: {
		type: Boolean,
		default: false
	},
	originalWidth: {
		type: Number,
		required: false
	},
	originalHeight: {
		type: Number,
		required: false
	},
	maxWidth: {
		type: Number,
		default: 600
	},
	maxHeight: {
		type: Number,
		default: 0.70
	},
	swiperId: {
		type: String,
		default: 'entry-media-swiper'
	},
	link: {
		type: String,
		default: null
	}
});

const imageContainer = ref(null);
const isLoaded = ref(false);
const lightbox = ref(null);
const explicitDismissed = ref(false);

const { proxy } = getCurrentInstance();

const log = (message, data = null) => {
	const payload = data ? `${message} ${JSON.stringify(data)}` : message;
	proxy?.$logger?.(payload);
};

const setNativeArticleSwipeLocked = (locked) => {
	log('app/component/lazy-image/setNativeArticleSwipeLocked fired', {
		locked,
		hasHelper: Boolean(proxy?.$helper),
		hasSendAppMessage: Boolean(proxy?.$helper?.sendAppMessage)
	});

	proxy?.$helper?.sendAppMessage({
		action: 'articleGallerySwipeLock',
		locked
	});

	log('app/component/lazy-image/setNativeArticleSwipeLocked sent', {
		locked
	});
};

const dismissExplicit = () => {
	explicitDismissed.value = true;
};

watch(
	() => [props.src, props.explicit],
	() => {
		explicitDismissed.value = false;
	}
);

const scaledHeight = computed(() => {
	const originalWidth = Number(props.originalWidth);
	const originalHeight = Number(props.originalHeight);
	const maxWidth = Number(props.maxWidth) || 600;
	const maxHeightRatio = Number(props.maxHeight) || 0.7;

	if (
		!Number.isFinite(originalWidth) ||
		!Number.isFinite(originalHeight) ||
		originalWidth <= 0 ||
		originalHeight <= 0
	) {
		return 500;
	}

	const containerWidth = imageContainer.value ? imageContainer.value.clientWidth : maxWidth;
	const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
	const newHeight = (originalHeight * containerWidth) / originalWidth;
	const maxHeight = viewportHeight * maxHeightRatio;

	return Math.min(newHeight, maxHeight);
});

const imageStyle = computed(() => {
	const height = Number(scaledHeight.value);

	return Number.isFinite(height) && height > 0
		? { height: `${height}px` }
		: {};
});

const observer = new IntersectionObserver(
	(entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				isLoaded.value = true;
				observer.unobserve(entry.target);
			}
		});
	},
	{
		root: null,
		rootMargin: '0px',
		threshold: 0.1
	}
);

const handleClick = () => {
	if (props.explicit && !explicitDismissed.value) {
		log('app/component/lazy-image/click blocked by explicit overlay');
		return;
	}

	if (props.link) {
		const a = document.createElement('a');
		a.target = '_blank';
		a.href = props.link;
		a.click();
	} else {
		toggleSwiper();
	}
};

const toggleSwiper = () => {
	log('app/component/lazy-image/toggleSwiper', {
		index: props.index,
		swiperId: props.swiperId
	});

	lightbox.value = new PhotoSwipeLightbox({
		gallery: '#' + props.swiperId,
		children: 'a',
		showHideAnimationType: 'none',
		pswpModule: () => import('photoswipe')
	});

	lightbox.value.on('beforeOpen', () => {
		log('app/component/lazy-image/photoswipe beforeOpen');
		setNativeArticleSwipeLocked(true);
	});

	lightbox.value.on('close', () => {
		log('app/component/lazy-image/photoswipe close');
		setNativeArticleSwipeLocked(false);
	});

	lightbox.value.on('destroy', () => {
		log('app/component/lazy-image/photoswipe destroy');
		setNativeArticleSwipeLocked(false);
	});

	lightbox.value.init();

	lightbox.value.loadAndOpen(props.index - 1, {
		gallery: document.querySelector('#' + props.swiperId)
	});

	log('app/component/lazy-image/loadAndOpen called', {
		index: props.index - 1
	});
};

const handleLoad = () => {
	isLoaded.value = true;
};

const handleError = (e) => {
	e.target.src = 'https://placehold.co/500x500/red/white?text=Afbeelding+stuk+jonge';
};

onMounted(() => {
	log('app/component/lazy-image/mounted', {
		index: props.index,
		swiperId: props.swiperId
	});

	if (imageContainer.value) {
		observer.observe(imageContainer.value);
	}
});

onUnmounted(() => {
	log('app/component/lazy-image/unmounted');

	setNativeArticleSwipeLocked(false);

	if (observer && imageContainer.value) {
		observer.unobserve(imageContainer.value);
	}

	observer.disconnect();

	if (lightbox.value) {
		lightbox.value.destroy();
		lightbox.value = null;
	}
});
</script>

<style>
.image-wrapper {
	position: relative;
}

.explicit-overlay {
	position: absolute;
	inset: 0;
	background: rgba(0, 0, 0, 0.95);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 5;
	padding: 16px;
}

.explicit-overlay__content {
	max-width: 420px;
	width: 100%;
	text-align: center;
	color: #fff;
}

.explicit-overlay__title {
	font-size: 16px;
	font-weight: 600;
	margin-bottom: 6px;
}

.explicit-overlay__text {
	font-size: 14px;
	opacity: 0.9;
	margin-bottom: 12px;
}

.explicit-fade-enter-active,
.explicit-fade-leave-active {
	transition: opacity 140ms ease-out;
}

.explicit-fade-enter-from,
.explicit-fade-leave-to {
	opacity: 0;
}
</style>
